import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  BanknoteArrowDown,
  BanknoteArrowUp,
  Boxes,
  ChartColumnBig,
  CircleDollarSign,
  ClipboardList,
  PackageSearch,
  RefreshCw,
  TrendingUp,
  WalletCards
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { ApiError, apiRequest } from "../api";
import { ErrorState, LoadingState } from "../components/PageState";
import { QuickSearch } from "../components/QuickSearch";
import { formatDate, formatMoney, formatNumber } from "../components/Ui";
import type { DashboardProductRanking, DashboardSummary } from "../types";

type TrendMetric = "revenue" | "collected" | "expenses" | "estimatedGrossProfit" | "orderCount";

const trendMetrics: Array<{ value: TrendMetric; label: string }> = [
  { value: "revenue", label: "Doanh thu giao hàng" },
  { value: "collected", label: "Tiền đã thu" },
  { value: "expenses", label: "Tổng chi" },
  { value: "estimatedGrossProfit", label: "Lợi nhuận gộp ước tính" },
  { value: "orderCount", label: "Số đơn đã giao" }
];

const quantityText = (rows: Array<{ uomName: string; quantity: number }>) =>
  rows.length ? rows.map((row) => `${formatNumber(row.quantity)} ${row.uomName}`).join(" · ") : "—";

function ProductTable({ rows, slow = false }: { rows: DashboardProductRanking[]; slow?: boolean }) {
  return (
    <div className="data-table-wrapper dashboard-table-wrapper">
      <table className="data-table dashboard-product-table">
        <thead>
          <tr>
            <th>Hạng</th><th>Mã/Tên sản phẩm</th>
            {slow ? <><th>Tồn hiện tại</th><th>Lần bán gần nhất</th><th className="number-cell">Giá trị tồn</th></> : <><th>Số lượng đã bán</th><th className="number-cell">Giá trị bán</th><th className="number-cell">Tỷ trọng</th></>}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.itemId}>
              <td><span className="rank-number">{index + 1}</span></td>
              <td><strong>{row.itemCode}</strong><small className="table-subtext">{row.itemName}</small></td>
              {slow ? (
                <>
                  <td>{quantityText(row.stock)}</td>
                  <td>{row.lastSaleDate ? formatDate(row.lastSaleDate) : "Chưa từng bán"}<small className="table-subtext">{row.daysWithoutSale != null ? `${formatNumber(row.daysWithoutSale)} ngày chưa bán` : "Hàng tồn không có lịch sử bán"}</small></td>
                  <td className="number-cell"><strong>{formatMoney(row.stockValue)}</strong>{row.nearestExpiry && <small className="table-subtext">HSD gần nhất: {formatDate(row.nearestExpiry)}</small>}</td>
                </>
              ) : (
                <>
                  <td>{quantityText(row.quantities)}<small className="table-subtext">{formatNumber(row.orderCount)} lần giao</small></td>
                  <td className="number-cell"><strong>{formatMoney(row.salesValue)}</strong></td>
                  <td className="number-cell">{formatNumber(row.revenueShare, 2)}%</td>
                </>
              )}
            </tr>
          ))}
          {!rows.length && <tr><td colSpan={5}><div className="empty-state">Chưa có dữ liệu phù hợp.</div></td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function DashboardPage() {
  const [, navigate] = useLocation();
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [mode, setMode] = useState<"month" | "year">("month");
  const [year, setYear] = useState(new Date().getFullYear());
  const [metric, setMetric] = useState<TrendMetric>("revenue");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setData(await apiRequest<DashboardSummary>(`/api/dashboard/summary?mode=${mode}&year=${year}`));
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Không tải được trang chủ.");
    } finally {
      setLoading(false);
    }
  }, [mode, year]);

  useEffect(() => { void load(); }, [load]);

  const chartValues = useMemo(() => data?.trend.map((row) => Number(row[metric]) || 0) ?? [], [data, metric]);
  const chartMaximum = Math.max(...chartValues, 1);
  const chartLabel = trendMetrics.find((row) => row.value === metric)?.label ?? "Giá trị";
  const formatChartValue = (value: number) => metric === "orderCount" ? formatNumber(value) : formatMoney(value);

  if (error && !data) return <ErrorState message={error} retry={load} />;
  if (!data) return <LoadingState />;

  const kpis = [
    { label: `Doanh thu ${year}`, value: data.kpis.revenue, note: `${data.kpis.revenueChangePercent >= 0 ? "+" : ""}${formatNumber(data.kpis.revenueChangePercent, 2)}% so với năm trước`, icon: TrendingUp, tone: data.kpis.revenueChangePercent >= 0 ? "teal" : "red" },
    { label: `Doanh thu ${year - 1}`, value: data.kpis.previousRevenue, note: "Kỳ so sánh", icon: ChartColumnBig, tone: "blue" },
    { label: "Tiền đã thu", value: data.kpis.collected, note: "Phiếu thu đã Hoàn tất", icon: BanknoteArrowDown, tone: "teal" },
    { label: "Phải thu còn lại", value: data.kpis.receivable, note: "Công nợ khách hàng", icon: WalletCards, tone: "gold" },
    { label: "Phải trả còn lại", value: data.kpis.payable, note: "Công nợ nhà cung cấp", icon: BanknoteArrowUp, tone: "violet" },
    { label: "Tổng chi", value: data.kpis.expenses, note: "Đã gồm phí ngân hàng", icon: CircleDollarSign, tone: "red" },
    { label: "Giá trị tồn ước tính", value: data.kpis.inventoryValue, note: "Theo giá nhập bình quân", icon: Boxes, tone: "blue" },
    { label: "Lợi nhuận gộp ước tính", value: data.kpis.estimatedGrossProfit, note: "Doanh thu trừ giá vốn ước tính", icon: PackageSearch, tone: "teal" }
  ];

  return (
    <div className="dashboard executive-dashboard">
      <section className="dashboard-title-row">
        <div>
          <span className="eyebrow">BẢNG ĐIỀU HÀNH v{data.version}</span>
          <h2>Tổng quan hoạt động GIA BẢO MEDICAL ERP</h2>
          <p>Dữ liệu lấy từ các chứng từ đã Hoàn tất; cảnh báo được cập nhật mỗi lần mở trang.</p>
        </div>
        <button className="secondary-button" onClick={() => void load()} disabled={loading}><RefreshCw size={17} className={loading ? "spin" : ""} /> Làm mới</button>
      </section>

      {error && <div className="form-message error"><AlertTriangle size={17} /><span>{error}</span></div>}

      <section className="executive-kpi-grid">
        {kpis.map(({ label, value, note, icon: Icon, tone }) => (
          <article className={`executive-kpi tone-${tone}`} key={label}>
            <div className="executive-kpi-icon"><Icon size={21} /></div>
            <span>{label}</span>
            <strong>{formatMoney(value)}</strong>
            <small>{note}</small>
          </article>
        ))}
      </section>

      <section className="panel trend-panel">
        <div className="panel-heading dashboard-panel-heading">
          <div><span className="eyebrow">XU HƯỚNG</span><h3>{chartLabel}</h3></div>
          <div className="dashboard-filters">
            <label>Hiển thị<select value={mode} onChange={(event) => setMode(event.target.value as "month" | "year")}><option value="month">Tháng 1–12</option><option value="year">10 năm gần nhất</option></select></label>
            <label>Năm<input type="number" min="2000" max="2100" value={year} onChange={(event) => setYear(Number(event.target.value) || new Date().getFullYear())} /></label>
            <label>Chỉ số<select value={metric} onChange={(event) => setMetric(event.target.value as TrendMetric)}>{trendMetrics.map((row) => <option value={row.value} key={row.value}>{row.label}</option>)}</select></label>
          </div>
        </div>
        <div className="dashboard-chart" role="img" aria-label={`Biểu đồ ${chartLabel}`}>
          {data.trend.map((row) => {
            const value = Number(row[metric]) || 0;
            const height = value <= 0 ? 2 : Math.max(7, (value / chartMaximum) * 100);
            return <div className="chart-column" key={row.label}><div className="chart-value">{formatChartValue(value)}</div><div className="chart-track"><div className="chart-bar" style={{ height: `${height}%` }} /></div><strong>{row.label}</strong></div>;
          })}
        </div>
        <div className="dashboard-notes">{data.notes.map((note) => <span key={note}>{note}</span>)}</div>
      </section>

      <section className="panel alerts-panel">
        <div className="panel-heading"><div><span className="eyebrow">VIỆC CẦN XỬ LÝ</span><h3>Cảnh báo điều hành</h3></div></div>
        <div className="dashboard-alert-grid">
          {data.alerts.map((alert) => (
            <button className={`dashboard-alert alert-${alert.severity}`} key={alert.key} onClick={() => navigate(alert.route)}>
              <AlertTriangle size={19} /><span><strong>{alert.count}</strong>{alert.label}<small>{alert.detail}</small></span><ArrowUpRight size={17} />
            </button>
          ))}
        </div>
      </section>

      <QuickSearch scope="next" value={search} onChange={setSearch} placeholder="Tìm mã hoặc tên sản phẩm trong bảng bán tốt/bán chậm..." resultCount={data.topProducts.length + data.slowProducts.length} />
      <div className="dashboard-ranking-grid">
        <section className="panel ranking-panel">
          <div className="panel-heading"><div><span className="eyebrow">HIỆU QUẢ BÁN HÀNG</span><h3>20 sản phẩm bán tốt nhất</h3><p>Xếp theo giá trị đã xuất giao trong năm {year}.</p></div><ArrowUpRight className="ranking-up" /></div>
          <ProductTable rows={data.topProducts} />
        </section>
        <section className="panel ranking-panel">
          <div className="panel-heading"><div><span className="eyebrow">HÀNG CẦN CHÚ Ý</span><h3>20 sản phẩm bán chậm nhất</h3><p>Chỉ lấy sản phẩm còn tồn; loại hàng mới và hàng đã hết tồn.</p></div><ArrowDownRight className="ranking-down" /></div>
          <ProductTable rows={data.slowProducts} slow />
        </section>
      </div>

      <section className="dashboard-help-strip"><ClipboardList size={20} /><span><strong>Cách đọc:</strong> dùng biểu đồ để xem xu hướng; bảng bán chậm để điều chỉnh sản xuất và chương trình bán; nhấn cảnh báo để đi thẳng đến phân hệ xử lý.</span></section>
    </div>
  );
}
